/**
 * Pure response-classifier for `/api/chat/verify`. Lives in its own
 * module so the chat-panel queryFn can stay a one-liner and so the
 * branching logic is unit-testable without standing up TanStack.
 *
 * The four definitive outcomes are returned as `VerifyResult` values;
 * the two transient outcomes (429 / 5xx / other unexpected statuses)
 * THROW so TanStack's retry pipeline picks them up and our global
 * `queryCache.onError` fires the "Reconnecting to network" overlay.
 *
 * Crucially, transient errors are NEVER returned as values — that's
 * the bug this rebuild fixes. The previous queryFn collapsed any
 * non-OK / non-403 response into `{ verified: false }`, which TanStack
 * happily cached for 60s as a "you don't hold a seat" answer, leaving
 * the panel stuck on the "Seat required" gate even after the upstream
 * (Envio) recovered.
 *
 * Network failures (`fetch` itself rejecting) propagate from the
 * caller and behave the same way: TanStack retries, the error bubbles
 * to `queryCache.onError`. We don't catch them here.
 */

import { WALLET_NOT_LINKED_CODE } from '@/lib/auth-codes';

export type VerifyResult = {
  verified: boolean;
  held_since: number | null;
  is_dev?: boolean;
  walletNotLinked?: boolean;
};

/**
 * Thrown for any response status the classifier treats as transient
 * (429, 5xx, or other unexpected codes). The status is preserved on
 * the instance for assertions in tests; the message includes the
 * literal status code so the global `isRateLimitLike` heuristic in
 * `query-provider.tsx` (which substring-matches "429") still triggers
 * the rate-limit overlay and backoff schedule.
 */
export class ChatVerifyTransientError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`chat-verify returned ${status}`);
    this.status = status;
    this.name = 'ChatVerifyTransientError';
  }
}

export async function classifyVerifyResponse(
  res: Response,
): Promise<VerifyResult> {
  if (res.status === 200) {
    return res.json() as Promise<VerifyResult>;
  }

  if (res.status === 403) {
    // Read the body once. If it carries the documented
    // `wallet_not_linked` code, surface the flag so the panel can
    // route to the SessionMismatch gate. Any other 403 (or a
    // malformed body) collapses to a definitive "not verified".
    const body = (await res.json().catch(() => null)) as
      | { error?: string }
      | null;
    if (body?.error === WALLET_NOT_LINKED_CODE) {
      return { verified: false, held_since: null, walletNotLinked: true };
    }
    return { verified: false, held_since: null };
  }

  if (res.status === 401) {
    return { verified: false, held_since: null };
  }

  // 429 / 5xx / unexpected 4xx: throw so TanStack retries with
  // backoff instead of caching `{ verified: false }`. The literal
  // "429" in the message is what `isRateLimitLike` in the global
  // query provider substring-matches on to drive the
  // "Reconnecting to network" overlay.
  throw new ChatVerifyTransientError(res.status);
}
